// 胡牌偵測（含特殊型結構分析）
// 對應 PRD §6.1 胡牌規則、§6.2 台數計算、§14.1 測試項
//
// 設計重點：
// 1. canHu(): 快速 boolean 判定，給 CRound 狀態機與 AI 反應策略用
// 2. findHuPatterns(): 列出所有可能的胡牌結構分解，給 scoring.ts 算台用
//
// 演算法：
// 標準台灣 16 張胡牌 = 5 個面子（順子/刻子/槓） + 1 個雀頭（對子）
// 副露區（hand.melds）裡每個 meld 已經算 1 個面子（包含槓），
// 所以待從暗牌（含 winTile）分解的面子數 = 5 - hand.melds.length。
//
// 暗牌分解採用「最小索引優先 + 回溯」：
//   1. 找到第一個 count > 0 的牌種
//   2. 嘗試當刻子（拿 3 張）→ 遞迴；失敗回復
//   3. 若是數牌且非花色尾端，嘗試當順子起頭 → 遞迴；失敗回復
//   4. 都不行 → 此分支失敗
//
// 雀頭在 outer loop 嘗試（每種牌都可能當雀頭，外層列舉 → 內層分解面子）。
//
// 為何用「索引化計數」（34 類）而非 TTile 物件陣列：
// 同一種牌（例：4 張 5 萬）在分解上等價，無視 id；用 count 大幅減少搜尋分支。

import type { THand } from './hand';
import type { TMeld, TMeldType } from './meld';
import { tileKey, tileToIndex, TILE_INDEX_TO_SUIT_RANK, type TTile } from './tile';
import type { TSuit } from './types';
import { MeldType } from '@core/meld';

// ============================================================
// 索引化編碼：把可參與胡牌結構的 34 種牌對應到 0..33
// ============================================================
// 0-8   man 1-9
// 9-17  pin 1-9
// 18-26 sou 1-9
// 27-30 wind east/south/west/north
// 31-33 dragon zhong/fa/bai
// (花牌不參與胡牌結構，分解前會過濾掉)

const HU_CATS = 34;

// 花牌（index ≥ 34）不參與胡牌結構，回 -1 排除；其餘直接用全牌種編碼。
const tileToHuIndex = (t: TTile): number => {
  const idx = tileToIndex(t);
  return idx < HU_CATS ? idx : -1;
};

// 0..33 反查表＝全牌種反查表的前 34 筆（花在 34 之後，本檔只會存取 0..33）。
const HU_INDEX_TO_SUIT_RANK = TILE_INDEX_TO_SUIT_RANK;

/** 是否為數牌索引（0..26）。只有數牌可以組順子。 */
const isNumericIndex = (i: number): boolean => i >= 0 && i <= 26;

// ============================================================
// 計數陣列輔助（避開 noUncheckedIndexedAccess 的 undefined 噪音）
// ============================================================

const getAt = (a: readonly number[], i: number): number => a[i] ?? 0;
const setAt = (a: number[], i: number, v: number): void => {
  a[i] = v;
};

const tilesToCounts = (tiles: readonly TTile[]): number[] => {
  const counts = new Array<number>(HU_CATS).fill(0);
  for (const t of tiles) {
    const idx = tileToHuIndex(t);
    if (idx >= 0) setAt(counts, idx, getAt(counts, idx) + 1);
  }
  return counts;
};

// ============================================================
// canHu — 快速 boolean 判定（早期回傳）
// ============================================================

/**
 * 在 counts 上嘗試把剩餘牌全部分解成 setsNeeded 個面子。
 * 採回溯：選最低索引的非零牌種，嘗試刻子或順子。
 *
 * 純讀寫 counts，不分配新陣列；遞迴前後 counts 必須維持平衡。
 */
const canFormSets = (counts: number[], setsNeeded: number): boolean => {
  if (setsNeeded === 0) {
    for (let k = 0; k < HU_CATS; k++) {
      if (getAt(counts, k) !== 0) return false;
    }
    return true;
  }

  // 找第一個非零索引
  let i = 0;
  while (i < HU_CATS && getAt(counts, i) === 0) i++;
  if (i === HU_CATS) return false; // 還缺面子但牌已用光

  // 嘗試 1：刻子（3 張同種）
  if (getAt(counts, i) >= 3) {
    setAt(counts, i, getAt(counts, i) - 3);
    if (canFormSets(counts, setsNeeded - 1)) {
      setAt(counts, i, getAt(counts, i) + 3);
      return true;
    }
    setAt(counts, i, getAt(counts, i) + 3);
  }

  // 嘗試 2：順子（必須是數牌、非花色尾端、後兩張可用）
  if (isNumericIndex(i)) {
    const rankInSuit = i % 9; // 0..8 對應 rank 1..9
    if (
      rankInSuit <= 6 &&
      getAt(counts, i + 1) >= 1 &&
      getAt(counts, i + 2) >= 1
    ) {
      setAt(counts, i, getAt(counts, i) - 1);
      setAt(counts, i + 1, getAt(counts, i + 1) - 1);
      setAt(counts, i + 2, getAt(counts, i + 2) - 1);
      if (canFormSets(counts, setsNeeded - 1)) {
        setAt(counts, i, getAt(counts, i) + 1);
        setAt(counts, i + 1, getAt(counts, i + 1) + 1);
        setAt(counts, i + 2, getAt(counts, i + 2) + 1);
        return true;
      }
      setAt(counts, i, getAt(counts, i) + 1);
      setAt(counts, i + 1, getAt(counts, i + 1) + 1);
      setAt(counts, i + 2, getAt(counts, i + 2) + 1);
    }
  }

  return false;
};

/**
 * 嘗試每種牌當雀頭，配合面子分解。
 * 任一組合成立即回 true。
 */
const canFormPairAndSets = (counts: number[], setsNeeded: number): boolean => {
  for (let i = 0; i < HU_CATS; i++) {
    if (getAt(counts, i) >= 2) {
      setAt(counts, i, getAt(counts, i) - 2);
      const ok = canFormSets(counts, setsNeeded);
      setAt(counts, i, getAt(counts, i) + 2);
      if (ok) return true;
    }
  }
  return false;
};

/**
 * 是否可胡牌（基本胡型）。
 *
 * @param hand 玩家手牌（含已副露的 melds）
 * @param winTile 完成胡牌的那張牌
 * @param isSelfDraw 是否自摸
 *   - true：winTile 已在 hand.concealed 中（剛抓進）
 *   - false：winTile 來自他家捨牌（榮和），尚未進 concealed
 *
 * 內部會驗證牌數正確性（concealed + winTile 應等於 (5 - melds) * 3 + 2）。
 */
export const canHu = (hand: THand, winTile: TTile, isSelfDraw: boolean): boolean => {
  const tilesForDecomp = isSelfDraw
    ? [...hand.concealed]
    : [...hand.concealed, winTile];

  const setsNeeded = 5 - hand.melds.length;
  if (setsNeeded < 0 || setsNeeded > 5) return false;

  // 排除花牌（理論上 hand.concealed 不該有花，但防禦性處理）
  const filtered = tilesForDecomp.filter((t) => t.suit !== 'flower');

  // 牌數檢查：5 個面子 + 1 雀頭 = setsNeeded * 3 + 2
  if (filtered.length !== setsNeeded * 3 + 2) return false;

  const counts = tilesToCounts(filtered);
  return canFormPairAndSets(counts, setsNeeded);
};

// ============================================================
// findHuPatterns — 列出所有結構分解（給 scoring 用）
// ============================================================

/** 一個面子的分類（含已副露的槓）const。 */
export const HuSetKind = {
  Run: 'run',
  Triplet: 'triplet',
  KongExposed: 'kong-exposed',
  KongConcealed: 'kong-concealed',
  KongAdded: 'kong-added',
} as const;

export type THuSetKind = (typeof HuSetKind)[keyof typeof HuSetKind];

/** 把 TMeldType 轉成 THuSetKind（吃 → run、碰 → triplet、其他保留）。 */
const meldTypeToHuSetKind = (mt: TMeldType): THuSetKind => {
  switch (mt) {
    case MeldType.Chi:
      return HuSetKind.Run;
    case MeldType.Pong:
      return 'triplet';
    default:
      return mt; // kong-exposed/concealed/added 同名直通
  }
};

/** 分解出來的一個面子（3 張順子/刻子，或 4 張槓）。 */
export type THuSet = {
  readonly kind: THuSetKind;
  readonly tiles: readonly TTile[];
  /**
   * 是否「暗」（牌全來自暗牌而非吃/碰/明槓副露）。
   * 暗牌分解出的面子 → true；副露面子 → false，但暗槓（KongConcealed）→ true。
   * 用於三／四／五暗刻判定（榮和補成的暗刻另在 scoring 端降為明刻）。
   */
  readonly concealed: boolean;
};

/**
 * 一組完整的胡牌結構分解。
 *
 * 一手牌可能有多種合法分解（例：4-5-6-7-8 既可拆成 4-5-6 + 7-8 也可拆成 5-6-7 + 4-8...
 * 不過 4-8 不是面子，所以這個例子不成立；但概念上多解確實存在）。
 *
 * scoring.ts 會挑出「台數最高」那組作為最終分數。
 */
export type THuPattern = {
  /** 雀頭兩張 */
  readonly pair: readonly [TTile, TTile];
  /** 5 個面子（含已副露 + 暗牌分解出的） */
  readonly sets: readonly THuSet[];
  /** 完成胡牌的那張牌（用於判別自摸/榮和、邊張、嵌張等） */
  readonly winTile: TTile;
  /** 是否自摸 */
  readonly isSelfDraw: boolean;
};

// ============================================================
// 列出所有面子分解的「簽章」（不含 TTile id，純結構）
// ============================================================

type TSetSignature =
  | { readonly kind: 'triplet'; readonly idx: number }
  | { readonly kind: 'run'; readonly firstIdx: number };

/** 列出 counts 上所有可能的面子分解（每組 = TSetSignature[]）。 */
const findAllSetDecomps = (counts: number[], setsNeeded: number): TSetSignature[][] => {
  if (setsNeeded === 0) {
    for (let k = 0; k < HU_CATS; k++) {
      if (getAt(counts, k) !== 0) return [];
    }
    return [[]];
  }

  let i = 0;
  while (i < HU_CATS && getAt(counts, i) === 0) i++;
  if (i === HU_CATS) return [];

  const results: TSetSignature[][] = [];

  // 刻子分支
  if (getAt(counts, i) >= 3) {
    setAt(counts, i, getAt(counts, i) - 3);
    for (const rest of findAllSetDecomps(counts, setsNeeded - 1)) {
      results.push([{ kind: 'triplet', idx: i }, ...rest]);
    }
    setAt(counts, i, getAt(counts, i) + 3);
  }

  // 順子分支
  if (isNumericIndex(i)) {
    const rankInSuit = i % 9;
    if (
      rankInSuit <= 6 &&
      getAt(counts, i + 1) >= 1 &&
      getAt(counts, i + 2) >= 1
    ) {
      setAt(counts, i, getAt(counts, i) - 1);
      setAt(counts, i + 1, getAt(counts, i + 1) - 1);
      setAt(counts, i + 2, getAt(counts, i + 2) - 1);
      for (const rest of findAllSetDecomps(counts, setsNeeded - 1)) {
        results.push([{ kind: 'run', firstIdx: i }, ...rest]);
      }
      setAt(counts, i, getAt(counts, i) + 1);
      setAt(counts, i + 1, getAt(counts, i + 1) + 1);
      setAt(counts, i + 2, getAt(counts, i + 2) + 1);
    }
  }

  return results;
};

/** 從 tile 池依條件挑指定種類 + 數量的牌（會從池中移除，供後續分配避免重用）。 */
const pickTiles = (
  pool: TTile[],
  predicate: (t: TTile) => boolean,
  count: number
): TTile[] => {
  const picked: TTile[] = [];
  for (let i = 0; i < pool.length && picked.length < count; i++) {
    const t = pool[i];
    if (t && predicate(t)) {
      picked.push(t);
      pool.splice(i, 1);
      i--;
    }
  }
  return picked;
};

/**
 * 把面子簽章與雀頭簽章還原為 THuSet（含真實 TTile）。
 *
 * 從 tile pool 依 suit/rank 貪心挑取，每挑出一張就從 pool 移除避免重用。
 * 失敗（pool 不夠）時回 null（理論上不該發生，因為簽章本來就是從 counts 推出的）。
 */
const buildPatternFromSignature = (
  pairIdx: number,
  setSigs: readonly TSetSignature[],
  meldSets: readonly THuSet[],
  unmeldedTiles: readonly TTile[],
  winTile: TTile,
  isSelfDraw: boolean
): THuPattern | null => {
  const pool = [...unmeldedTiles];

  // 1. 挑雀頭
  const pairSpec = HU_INDEX_TO_SUIT_RANK[pairIdx];
  if (!pairSpec) return null;
  const pairTiles = pickTiles(
    pool,
    (t) => t.suit === pairSpec.suit && t.rank === pairSpec.rank,
    2
  );
  if (pairTiles.length !== 2) return null;

  // 2. 依簽章挑面子
  const decomposedSets: THuSet[] = [];
  for (const sig of setSigs) {
    if (sig.kind === 'triplet') {
      const spec = HU_INDEX_TO_SUIT_RANK[sig.idx];
      if (!spec) return null;
      const tiles = pickTiles(
        pool,
        (t) => t.suit === spec.suit && t.rank === spec.rank,
        3
      );
      if (tiles.length !== 3) return null;
      decomposedSets.push({ kind: 'triplet', tiles, concealed: true });
    } else {
      const spec0 = HU_INDEX_TO_SUIT_RANK[sig.firstIdx];
      const spec1 = HU_INDEX_TO_SUIT_RANK[sig.firstIdx + 1];
      const spec2 = HU_INDEX_TO_SUIT_RANK[sig.firstIdx + 2];
      if (!spec0 || !spec1 || !spec2) return null;
      const t0 = pickTiles(pool, (t) => t.suit === spec0.suit && t.rank === spec0.rank, 1);
      const t1 = pickTiles(pool, (t) => t.suit === spec1.suit && t.rank === spec1.rank, 1);
      const t2 = pickTiles(pool, (t) => t.suit === spec2.suit && t.rank === spec2.rank, 1);
      if (t0.length !== 1 || t1.length !== 1 || t2.length !== 1) return null;
      decomposedSets.push({
        kind: 'run',
        tiles: [t0[0] as TTile, t1[0] as TTile, t2[0] as TTile],
        concealed: true,
      });
    }
  }

  return {
    pair: [pairTiles[0] as TTile, pairTiles[1] as TTile],
    sets: [...meldSets, ...decomposedSets],
    winTile,
    isSelfDraw,
  };
};

/**
 * 列出所有可能的胡牌結構分解。
 *
 * 不能胡時回傳空陣列。
 *
 * 為什麼可能有多解？
 * 同一手牌可以有多種合法的「面子＋雀頭」組合，例：
 *   2-3-4-5-6 萬 + 5 萬雀頭 → (2-3-4) + (5-6) ❌ 不成立
 *   實際上多解多出現於混色或多刻子情境。
 * scoring 階段會挑「台數最高」者。
 */
export const findHuPatterns = (
  hand: THand,
  winTile: TTile,
  isSelfDraw: boolean
): THuPattern[] => {
  const setsNeeded = 5 - hand.melds.length;
  if (setsNeeded < 0 || setsNeeded > 5) return [];

  const unmelded = isSelfDraw
    ? [...hand.concealed]
    : [...hand.concealed, winTile];
  const filtered = unmelded.filter((t) => t.suit !== 'flower');
  if (filtered.length !== setsNeeded * 3 + 2) return [];

  const counts = tilesToCounts(filtered);

  // 已副露面子（含槓）轉成 THuSet 直接帶入
  const meldSets: THuSet[] = hand.melds.map((m: TMeld) => ({
    kind: meldTypeToHuSetKind(m.type),
    tiles: [...m.tiles],
    concealed: m.type === MeldType.KongConcealed, // 暗槓算暗，其餘副露算明
  }));

  // 列舉每種牌當雀頭的可能性
  const patterns: THuPattern[] = [];
  for (let i = 0; i < HU_CATS; i++) {
    if (getAt(counts, i) < 2) continue;

    setAt(counts, i, getAt(counts, i) - 2);
    const setDecomps = findAllSetDecomps(counts, setsNeeded);
    setAt(counts, i, getAt(counts, i) + 2);

    for (const sig of setDecomps) {
      const pattern = buildPatternFromSignature(i, sig, meldSets, filtered, winTile, isSelfDraw);
      if (pattern) patterns.push(pattern);
    }
  }

  // 去重：簽章相同的 pattern 只保留一份（pair + sets 結構）
  const seen = new Set<string>();
  const unique: THuPattern[] = [];
  for (const p of patterns) {
    const key = `${tileKey(p.pair[0])}|${p.sets
      .map((s) => `${s.kind}:${s.tiles.map(tileKey).sort().join(',')}`)
      .sort()
      .join('|')}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(p);
    }
  }

  return unique;
};

// ============================================================
// 結構查詢（給 scoring 用）
// ============================================================

/** 一手 pattern 中含有的所有 numeric suit（萬筒條）。 */
export const numericSuitsOf = (p: THuPattern): Set<TSuit> => {
  const s = new Set<TSuit>();
  const collect = (tiles: readonly TTile[]) => {
    for (const t of tiles) {
      if (t.suit === 'man' || t.suit === 'pin' || t.suit === 'sou') s.add(t.suit);
    }
  };
  collect(p.pair);
  for (const set of p.sets) collect(set.tiles);
  return s;
};

/** 是否含字牌（風或元）。 */
export const hasHonorTiles = (p: THuPattern): boolean => {
  const check = (t: TTile) => t.suit === 'wind' || t.suit === 'dragon';
  if (p.pair.some(check)) return true;
  return p.sets.some((s) => s.tiles.some(check));
};

/** 是否全部面子皆為刻子或槓（碰碰胡判定基礎）。 */
export const isAllTripletsOrKongs = (p: THuPattern): boolean =>
  p.sets.every(
    (s) =>
      s.kind === 'triplet' ||
      s.kind === HuSetKind.KongExposed ||
      s.kind === HuSetKind.KongConcealed ||
      s.kind === HuSetKind.KongAdded
  );

/** 是否全部面子皆為順子（平胡判定基礎，雀頭另外檢）。 */
export const isAllRuns = (p: THuPattern): boolean =>
  p.sets.every((s) => s.kind === 'run');

/**
 * 統計三元牌（中發白）出現情況。
 * 回傳 {triplets: 0..3, hasPair: boolean}，用於小三元/大三元判定。
 */
export const dragonStats = (p: THuPattern): { triplets: number; hasPair: boolean } => {
  let triplets = 0;
  for (const set of p.sets) {
    const first = set.tiles[0];
    if (!first || first.suit !== 'dragon') continue;
    if (
      set.kind === 'triplet' ||
      set.kind === HuSetKind.KongExposed ||
      set.kind === HuSetKind.KongConcealed ||
      set.kind === HuSetKind.KongAdded
    ) {
      triplets++;
    }
  }
  const hasPair = p.pair[0]?.suit === 'dragon';
  return { triplets, hasPair };
};

/** 是否為「刻子或槓」類面子（暗刻 / 明刻 / 各種槓）。 */
const isTripletLike = (s: THuSet): boolean =>
  s.kind === 'triplet' ||
  s.kind === HuSetKind.KongExposed ||
  s.kind === HuSetKind.KongConcealed ||
  s.kind === HuSetKind.KongAdded;

/**
 * 統計風牌（東南西北）刻子情況。
 * 回傳 {triplets: 0..4, hasPair}，用於小四喜 / 大四喜判定。
 */
export const windStats = (p: THuPattern): { triplets: number; hasPair: boolean } => {
  let triplets = 0;
  for (const set of p.sets) {
    if (set.tiles[0]?.suit === 'wind' && isTripletLike(set)) triplets++;
  }
  return { triplets, hasPair: p.pair[0]?.suit === 'wind' };
};

/** 是否字一色（全部牌皆為風或元，無數牌）。 */
export const isAllHonors = (p: THuPattern): boolean => {
  const honor = (t: TTile) => t.suit === 'wind' || t.suit === 'dragon';
  if (!p.pair.every(honor)) return false;
  return p.sets.every((s) => s.tiles.every(honor));
};

/**
 * 計算暗刻 / 暗槓數（三／四／五暗刻判定基礎）。
 * 暗 = THuSet.concealed 為 true 的刻子或槓；
 * 榮和（非自摸）補成的那組暗刻其實是明刻，扣除 1。
 */
export const concealedTripletCount = (p: THuPattern): number => {
  let n = 0;
  for (const set of p.sets) {
    if (set.concealed && isTripletLike(set)) n++;
  }
  if (!p.isSelfDraw) {
    // 榮和那張落在某組暗刻 → 該組降為明刻
    const w = p.winTile;
    for (const set of p.sets) {
      if (set.concealed && isTripletLike(set) && set.tiles.some((t) => t.id === w.id)) {
        n -= 1;
        break;
      }
    }
  }
  return n;
};

/** 胡的那張牌完成的等待型態（邊張 / 嵌張 / 單吊 / 兩面 / 其他）const。 */
export const WaitKind = {
  Edge: 'edge', // 邊張：12 聽 3、89 聽 7
  Closed: 'closed', // 嵌張：1_3 聽 2
  Pair: 'pair', // 單吊：吊將眼
  Open: 'open', // 兩面：無額外台
  None: 'none', // 刻子/槓 補成，或無法判定
} as const;

export type TWaitKind = (typeof WaitKind)[keyof typeof WaitKind];

/** 判定胡的那張牌在此分解中的等待型態。 */
export const winWaitKind = (p: THuPattern): TWaitKind => {
  const w = p.winTile;
  if (p.pair.some((t) => t.id === w.id)) return WaitKind.Pair;
  for (const set of p.sets) {
    if (!set.tiles.some((t) => t.id === w.id)) continue;
    if (set.kind !== 'run') return WaitKind.None; // 刻子/槓 完成，無邊嵌單吊台
    const ranks = set.tiles.map((t) => t.rank).slice().sort((a, b) => a - b);
    if (w.rank === ranks[1]) return WaitKind.Closed; // 中間 → 嵌張
    if (ranks[0] === 1 && ranks[2] === 3 && w.rank === 3) return WaitKind.Edge; // 12 聽 3
    if (ranks[0] === 7 && ranks[2] === 9 && w.rank === 7) return WaitKind.Edge; // 89 聽 7
    return WaitKind.Open; // 兩面
  }
  return WaitKind.None;
};
